/*
 * @Author: youfanrong 737064439@qq.com
 * @Date: 2025-05-14 12:26:56
 * @LastEditors: youfanrong 737064439@qq.com
 * @LastEditTime: 2025-09-02 17:18:40
 * @FilePath: /bar-front-end/src/utils/wsModule.ts
 */

import { useUserStoreWithOut } from '@/store/modules/user'
import router from '@/router'
import {
  print,
  onElectronMessage,
  isInElectron,
  testMessageListening,
  testConnectionCheck
} from '@/utils/electronBridge'
import { ElNotification, ElButton } from 'element-plus'
import { h } from 'vue'
import { printOrderApi, pullPrintTasksApi, ackPrintTaskApi } from '@/api/order'

class WsModule {
  ws: any = null
  token: string | null = null
  wsUrl: string = import.meta.env.VITE_APP_WS_URL
  reconnectAttempts: number = 0
  maxReconnectAttempts: number = 5
  reconnectTimer: any = null

  // ===== 打印任务可靠投递相关 =====
  // 兜底轮询定时器：即使WebSocket信号丢失，也会定期拉取待打印任务
  pollTimer: any = null
  // 轮询间隔(毫秒)
  pollIntervalMs: number = 20000
  // 是否正在拉取，避免并发拉取导致重复打印
  isPulling: boolean = false
  // 已打印任务ID集合，用于本地去重(防止同一任务重复打印)
  printedTaskIds: Set<string> = new Set()
  // 本地去重持久化的key与最大保留数量
  printedTaskIdsKey: string = 'printedTaskIds'
  maxPrintedTaskIds: number = 500

  constructor() {
    // 恢复本地已打印任务记录，避免刷新后重复打印
    this.loadPrintedTaskIds()
    // 延迟初始化，确保 DOM 和 electronBridge 都已准备好
    if (typeof window !== 'undefined') {
      // 如果在浏览器环境中，等待 DOM 加载完成
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.initElectronMessageListener()
        })
      } else {
        // DOM 已经加载完成，延迟一点时间确保 electronBridge 准备好
        setTimeout(() => {
          this.initElectronMessageListener()
        }, 100)
      }
    } else {
      // 非浏览器环境，直接初始化
      this.initElectronMessageListener()
    }
  }

  // 初始化 Electron 消息监听
  initElectronMessageListener() {
    console.log('=== WsModule 初始化 Electron 消息监听 ===')
    console.log('当前时间:', new Date().toISOString())
    console.log('document.readyState:', document.readyState)

    // 先测试 electronBridge 的状态
    testMessageListening()

    console.log('isInElectron:', isInElectron())
    if (isInElectron()) {
      console.log('在 Electron 环境中，开始监听消息')
      onElectronMessage((message) => {
        console.log('=== WsModule 收到 Electron 消息 ===')
        console.log('消息内容：', message)
        console.log('消息类型：', typeof message)
        this.handleElectronMessage(message)
      })

      // 添加额外的测试方法
      console.log('消息监听设置完成，可以通过以下方式测试：')
      console.log('1. window.wsModule.testElectronMessage()')
      console.log('2. 发送 check-websocket-connection 消息')
    } else {
      console.log('不在 Electron 环境中，跳过消息监听')
    }
  }

  // 处理 Electron 消息
  handleElectronMessage(message: any) {
    console.log('处理 Electron 消息：', message, typeof message)

    // 如果消息是字符串，尝试解析
    let parsedMessage = message
    if (typeof message === 'string') {
      try {
        parsedMessage = JSON.parse(message)
      } catch (e) {
        // 如果不是 JSON，直接使用字符串值
        parsedMessage = { action: message }
      }
    }

    // 检查消息类型
    if (
      parsedMessage.action === 'check-websocket-connection' ||
      message === 'check-websocket-connection'
    ) {
      console.log('收到检查 WebSocket 连接的消息，准备重连')
      this.checkAndReconnectWebSocket()
    }
  }

  // 检查 WebSocket 连接状态并重连
  checkAndReconnectWebSocket() {
    console.log('检查 WebSocket 连接状态...')

    // 检查当前连接状态
    if (
      !this.ws ||
      this.ws.readyState === WebSocket.CLOSED ||
      this.ws.readyState === WebSocket.CLOSING
    ) {
      console.log('WebSocket 连接已断开，开始重连...')
      this.reconnectWebSocket()
    } else if (this.ws.readyState === WebSocket.CONNECTING) {
      console.log('WebSocket 正在连接中...')
    } else if (this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket 连接正常')
      // 连接正常，但为了确保稳定性，也可以选择重连
      // 这里根据实际需求决定是否要强制重连
      console.log('强制重连以确保连接稳定性')
      this.reconnectWebSocket()
    }
  }

  // 重连 WebSocket
  reconnectWebSocket() {
    console.log('开始重连 WebSocket...')

    // 先关闭现有连接
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    // 清除重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    // 重置重连次数
    this.reconnectAttempts = 0

    // 重新连接
    this.connectWebSocket()
  }

  // 提供一个测试方法，方便调试
  testElectronMessage() {
    console.log('测试 Electron 消息处理')
    this.handleElectronMessage('check-websocket-connection')
  }

  // 手动重新初始化消息监听（调试用）
  reinitMessageListener() {
    console.log('手动重新初始化消息监听')
    this.initElectronMessageListener()
  }

  // 获取当前状态（调试用）
  getStatus() {
    return {
      wsState: this.ws ? this.ws.readyState : 'null',
      isInElectron: isInElectron(),
      electronBridge: !!window.electronBridge,
      electronBridgeMethods: window.electronBridge ? Object.keys(window.electronBridge) : []
    }
  }

  // 测试连接检查（调试用）
  testConnectionCheck() {
    console.log('WsModule 测试连接检查')
    testConnectionCheck()
  }

  // 连接WebSocket
  connectWebSocket() {
    const userStore = useUserStoreWithOut()
    const token = userStore.token
    this.token = token
    console.log('token=======', this.ws, token)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket 已连接，跳过重复连接')
      return // 防止重复连接
    }
    if (!token) {
      console.log('token为空，无法连接WebSocket')
      this.closeWebSocket()
      // 跳转登录页面
      router.push('/login')
      return
    }
    console.log('连接中')
    this.ws = new WebSocket(`${this.wsUrl}/barV2/frontend/ws?token=${token}`)
    // this.ws = new WebSocket(`ws://front.v2.sounding-club.cn/barV2/frontend/ws?token=${token}`)

    this.ws.onopen = () => {
      console.log('WebSocket 连接成功')
      // 重置重连次数
      this.reconnectAttempts = 0
      // 清除重连定时器
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
      // 连接成功后立即补拉一次断线期间可能漏掉的打印任务
      this.pullAndPrintTasks()
      // 启动兜底轮询
      this.startPolling()
    }

    this.ws.onmessage = (data) => {
      console.log('收到消息：', data)
      this.handleMessage(data)
    }

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error)
    }

    this.ws.onclose = (event) => {
      console.log('WebSocket 连接关闭', event)
      if (event.code === 1000) {
        console.log('WebSocket 主动关闭')
        this.ws = null
      } else {
        // 仅在非主动关闭时尝试重连
        console.log('尝试重连...')
        // 重连逻辑
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectTimer = setTimeout(() => {
            console.log(`尝试重连 ${this.reconnectAttempts + 1}`)
            this.connectWebSocket()
            this.reconnectAttempts++
          }, 5000)
        } else {
          console.log('达到最大重连次数，停止重连')
          this.ws = null
        }
      }
    }
  }

  // 关闭WebSocket
  closeWebSocket() {
    this.stopPolling()
    if (this.ws) {
      this.ws.close(1000, '主动关闭')
      this.ws = null
    }
  }

  // 打印订单
  printOrder(data) {
    console.log('打印数据：', data)
  }

  // 处理消息(WebSocket 仅用于轻量信号与即时通知，真正的打印数据通过拉取打印任务接口获取)
  async handleMessage(data) {
    console.log('收到的websocket数据：', data)
    const dataObj = JSON.parse(data.data)
    switch (dataObj.msgType) {
      case 0: // 纯文字提示
        console.log('纯文字提示：', dataObj)
        break
      case 6: // 有新打印任务的信号，立即拉取打印任务
        console.log('收到新打印任务信号，开始拉取打印任务')
        this.pullAndPrintTasks()
        break
      case 3: // 小程序取酒申请触发
        {
          const notification = ElNotification({
            title: '取酒申请',
            // message: '有新的取酒申请，请及时审核',
            message: () =>
              h(
                'div',
                {
                  style: {}
                },
                {
                  default: () => [
                    h(
                      'div',
                      {
                        style: {
                          marginBottom: '10px'
                        }
                      },
                      '有新的取酒申请，请及时审核'
                    ),
                    h(
                      ElButton,
                      {
                        type: 'primary',
                        onClick: () => {
                          router.push('/save-take-wine/take-wine-list')
                          // 关闭ElNotification
                          notification.close()
                        }
                      },
                      {
                        default: () => '去审核'
                      }
                    )
                  ]
                }
              ),
            duration: 0,
            type: 'warning'
          })
        }
        break

      case 4: // 打印结账单(改为通过拉取打印任务处理，此处不再直接打印)
        {
          console.log('收到打印结账单信号，将通过拉取打印任务处理：', dataObj)
        }
        break
      case 5: // 打印存酒单(改为通过拉取打印任务处理，此处不再直接打印)
        {
          console.log('收到打印存酒单信号，将通过拉取打印任务处理：', dataObj)
        }
        break
      default:
        break
    }
  }

  // ===== 打印任务可靠投递：拉取 / 打印 / ACK / 去重 / 轮询 =====

  // 拉取并打印待处理的打印任务
  async pullAndPrintTasks() {
    if (this.isPulling) {
      console.log('正在拉取打印任务，跳过本次触发')
      return
    }
    this.isPulling = true
    try {
      const res: any = await pullPrintTasksApi()
      if (!res || res.code !== 200 || !Array.isArray(res.data)) {
        return
      }
      console.log(`拉取到 ${res.data.length} 个待打印任务`)
      // 顺序处理，保证打印顺序
      for (const task of res.data) {
        await this.handlePrintTask(task)
      }
    } catch (e) {
      console.error('拉取打印任务失败：', e)
    } finally {
      this.isPulling = false
    }
  }

  // 处理单个打印任务（含本地去重与ACK）
  async handlePrintTask(task: any) {
    if (!task || !task.taskId) return
    const taskId = task.taskId

    // 本地去重：已打印过则只补一次ACK（防止之前ACK丢失导致服务端仍为未完成）
    if (this.printedTaskIds.has(taskId)) {
      console.log('打印任务已打印过，补发ACK并跳过：', taskId)
      await this.ackTask(taskId, true)
      return
    }

    try {
      await print({ ...task.data, msgType: task.msgType })
      // 先记录本地去重，再ACK，避免ACK成功但本地未记录导致的重复打印
      this.markPrinted(taskId)
      await this.ackTask(taskId, true)

      // 兼容原有出品单"出单"业务标记（msgType=1）
      if (task.msgType === 1 && task.data && task.data.pdepOrderId) {
        try {
          const res: any = await printOrderApi({ pdepOrderId: task.data.pdepOrderId })
          if (res && res.code === 200) {
            console.log('出单成功')
          }
        } catch (e) {
          console.warn('出单标记失败（不影响打印）：', e)
        }
      }
    } catch (e) {
      console.error('打印任务执行失败：', taskId, e)
      await this.ackTask(taskId, false)
    }
  }

  // 回报打印结果
  async ackTask(taskId: string, success: boolean) {
    try {
      await ackPrintTaskApi({ taskId, success })
    } catch (e) {
      console.error('打印任务ACK失败：', taskId, e)
    }
  }

  // 记录已打印任务ID并持久化（限制最大数量）
  markPrinted(taskId: string) {
    this.printedTaskIds.add(taskId)
    let arr = Array.from(this.printedTaskIds)
    if (arr.length > this.maxPrintedTaskIds) {
      arr = arr.slice(arr.length - this.maxPrintedTaskIds)
      this.printedTaskIds = new Set(arr)
    }
    try {
      localStorage.setItem(this.printedTaskIdsKey, JSON.stringify(arr))
    } catch (e) {
      // localStorage 不可用时忽略
    }
  }

  // 从本地恢复已打印任务ID
  loadPrintedTaskIds() {
    try {
      const raw = localStorage.getItem(this.printedTaskIdsKey)
      if (raw) {
        const arr = JSON.parse(raw)
        if (Array.isArray(arr)) {
          this.printedTaskIds = new Set(arr)
        }
      }
    } catch (e) {
      this.printedTaskIds = new Set()
    }
  }

  // 启动兜底轮询（WebSocket信号丢失时仍能拉取到打印任务）
  startPolling() {
    if (this.pollTimer) return
    this.pollTimer = setInterval(() => {
      this.pullAndPrintTasks()
    }, this.pollIntervalMs)
    console.log('打印任务兜底轮询已启动，间隔(ms)：', this.pollIntervalMs)
  }

  // 停止兜底轮询
  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
      console.log('打印任务兜底轮询已停止')
    }
  }
}

const wsModuleInstance = new WsModule()

// 在开发环境下将实例挂载到 window 对象上，方便调试
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  ;(window as any).wsModule = wsModuleInstance
  // 也挂载一些测试函数
  ;(window as any).testConnectionCheck = testConnectionCheck
  ;(window as any).testMessageListening = testMessageListening
  console.log('WsModule 实例已挂载到 window.wsModule，可用于调试')
  console.log('测试函数已挂载：window.testConnectionCheck, window.testMessageListening')
}

export default wsModuleInstance
